import React, { useState } from 'react';
import {
  LinearProgress,
  Box,
  Button,
  Grid,
  Modal,
  Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { darken } from 'polished';
import { FormPublicDesignFragment } from 'graphql/fragment/form.public.fragment';
import { StyledButton } from './styled/button';

interface Props {
  progressValue: number;
  steps: number;
  design: FormPublicDesignFragment;
  reset: (newSubmission: boolean) => void;
  paddingBottom?: string;
}

export const ProgressHeader: React.FC<Props> = ({
  progressValue,
  steps,
  design,
  reset,
  paddingBottom = '35px',
}) => {
  // ticket #239 makes all colors in database 6 string hexcodes but not merged yet
  // some forms have 3 string hexcodes, need to convert to 6 for gradient
  const revisedBackground =
    design.colors.background === '#FFF' || design.colors.background === '#000'
      ? design.colors.background + design.colors.background.substring(1)
      : design.colors.background;
  const [openModal, setOpenModal] = useState(false);
  const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: ['80%', 400],
    backgroundColor: design.colors.background,
    border: `2px solid ${design.colors.question}`,
    borderRadius: '8px',
    boxShadow: 24,
    padding: ['5%', 4],
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        background: `linear-gradient(180deg, ${revisedBackground + 'FF'} 50%, 
        ${revisedBackground + '00'} 100%)`, // add 2 digits to 6 hexcode for opacity (gradient purposes)
        zIndex: 2, // zpaddingBottom2 for multiple choice/checkbox since for some reason they have zIndex 1
        paddingBottom: paddingBottom,
      }}
    >
      <LinearProgress
        variant='determinate'
        value={progressValue}
        sx={{
          backgroundColor: darken(0.2, design.colors.button),
          '& .MuiLinearProgress-bar': {
            backgroundColor: design.colors.button,
          },
          height: 10,
        }}
      />
      <Box
        sx={{
          justifyContent: 'right',
          display: 'flex',
        }}
      >
        <Button onClick={() => setOpenModal(true)}>
          <Typography color='gray' fontSize='14px'>
            RESET FORM
          </Typography>
          <RestartAltIcon sx={{ color: 'gray' }} fontSize='medium' />
        </Button>
      </Box>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby='modal-reset-form'
      >
        <Grid
          container
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={modalStyle}
        >
          <Grid item xs={12} sx={{ marginBottom: '3rem' }}>
            <Typography textAlign='center' color={design.colors.question}>
              Resetting will delete all of the current inputs. Do you want to
              proceed?
            </Typography>
          </Grid>
          <Grid item>
            <StyledButton
              onClick={() => setOpenModal(false)}
              background={design.colors.buttonText}
              $buttonColor={design.colors.button}
              highlight={design.colors.buttonActive}
              className={'modal-button'}
            >
              Cancel
            </StyledButton>
          </Grid>
          <Grid item>
            <StyledButton
              onClick={() => {
                reset(false);
                setOpenModal(false);
              }}
              background={design.colors.button}
              $buttonColor={design.colors.buttonText}
              highlight={design.colors.buttonActive}
              className={'modal-button'}
            >
              Yes
            </StyledButton>
          </Grid>
        </Grid>
      </Modal>
    </Box>
  );
};
